import React, { createContext, useContext, useState, useCallback } from 'react';

const WalletContext = createContext(null);

const formatAmount = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const INITIAL_BALANCE = 18224.0;

// Seed transactions use translation KEYS — the active screen looks them up against `t`.
// External identifiers like SimoPay IDs stay as raw strings.
const seedTransactions = () => {
  const now = Date.now();
  return [
    { id: 'tx-1', icon: '☕', nameKey: 'txCafeBoulevard',                rawAmount: -38.0,  positive: false, categoryKey: 'catFood',     timestamp: now - 1 * 60 * 60 * 1000 },
    { id: 'tx-2', icon: '💳', nameKey: 'txTopUpPrefix', methodKey: 'debitCard',     rawAmount: 500.0, positive: true, categoryKey: 'catTopUp',    timestamp: now - 1 * 60 * 60 * 1000 - 60 * 1000 },
    { id: 'tx-3', icon: '🛒', nameKey: 'txMarjane',                     rawAmount: -245.5, positive: false, categoryKey: 'catShopping', timestamp: now - 2 * 60 * 60 * 1000 },
    { id: 'tx-4', icon: '🏦', nameKey: 'txTopUpPrefix', methodKey: 'bankTransfer',  rawAmount: 5000.0,positive: true, categoryKey: 'catTopUp',    timestamp: now - 26 * 60 * 60 * 1000 },
    { id: 'tx-5', icon: '👤', name: 'hassan.benali@simopay',            rawAmount: -100.0, positive: false, categoryKey: 'catTransfer', timestamp: now - 2 * 24 * 60 * 60 * 1000 },
    { id: 'tx-6', icon: '🏪', nameKey: 'txCafeAtlas',                   rawAmount: -75.0,  positive: false, categoryKey: 'catFood',     timestamp: now - 2 * 24 * 60 * 60 * 1000 - 30 * 60 * 1000 },
    { id: 'tx-7', icon: '💳', nameKey: 'txTopUpPrefix', methodKey: 'debitCard',     rawAmount: 2000.0,positive: true, categoryKey: 'catTopUp',    timestamp: now - 3 * 24 * 60 * 60 * 1000 },
    { id: 'tx-8', icon: '👤', name: 'fatima.zahra@simopay',             rawAmount: -500.0, positive: false, categoryKey: 'catTransfer', timestamp: now - 4 * 24 * 60 * 60 * 1000 },
  ];
};

export const WalletProvider = ({ children }) => {
  const [balance, setBalance] = useState(INITIAL_BALANCE);
  const [transactions, setTransactions] = useState(seedTransactions());
  /** SIMO Card demo profile — null until user applies */
  const [simoCard, setSimoCard] = useState(null);

  // Decorate each tx with formatted amount only — the consumer formats `time` and resolves
  // `name` / `category` translations using its own `t` and a tiny helper below.
  const decoratedTransactions = transactions.map((tx) => ({
    ...tx,
    amount: `${tx.positive ? '+' : '-'}${formatAmount(Math.abs(tx.rawAmount))}`,
  }));

  const _addTransaction = useCallback((tx) => {
    const id = `tx-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    setTransactions((prev) => [{ id, timestamp: Date.now(), ...tx }, ...prev]);
  }, []);

  // Top up — increases balance + adds a credit transaction.
  // `methodKey` is one of: 'bankTransfer' | 'debitCard' | 'cashDeposit'
  const topUp = useCallback((amountStr, methodKey) => {
    const amt = parseFloat(amountStr);
    if (!amt || amt <= 0) return;
    setBalance((b) => b + amt);
    const icon =
      methodKey === 'bankTransfer' ? '🏦'
      : methodKey === 'cashDeposit' ? '💵'
      : '💳';
    _addTransaction({
      icon,
      nameKey: 'txTopUpPrefix',
      methodKey,
      rawAmount: amt,
      positive: true,
      categoryKey: 'catTopUp',
    });
  }, [_addTransaction]);

  const sendMoney = useCallback((amountStr, contact) => {
    const amt = parseFloat(amountStr);
    if (!amt || amt <= 0) return;
    setBalance((b) => b - amt);
    _addTransaction({
      icon: '👤',
      name: contact?.simoId || contact?.name || 'Transfer',
      rawAmount: -amt,
      positive: false,
      categoryKey: 'catTransfer',
    });
  }, [_addTransaction]);

  const payQR = useCallback((amountStr, target) => {
    const amt = parseFloat(amountStr);
    if (!amt || amt <= 0) return;
    setBalance((b) => b - amt);
    const isMerchant = target?.kind === 'merchant';
    if (isMerchant && target?.nameKey) {
      _addTransaction({
        icon: '🏪',
        nameKey: target.nameKey,
        rawAmount: -amt,
        positive: false,
        categoryKey: 'catFood',
      });
    } else {
      _addTransaction({
        icon: isMerchant ? '🏪' : '👤',
        name: target?.name || target?.simoId || 'QR Payment',
        rawAmount: -amt,
        positive: false,
        categoryKey: isMerchant ? 'catFood' : 'catTransfer',
      });
    }
  }, [_addTransaction]);

  const receiveMoney = useCallback((amountStr, fromName) => {
    const amt = parseFloat(amountStr);
    if (!amt || amt <= 0) return;
    setBalance((b) => b + amt);
    _addTransaction({
      icon: '👤',
      name: fromName || 'Received',
      rawAmount: amt,
      positive: true,
      categoryKey: 'catTransfer',
    });
  }, [_addTransaction]);

  const applySimoCard = useCallback((kind) => {
    const k = kind === 'physical' ? 'physical' : 'virtual';
    setSimoCard({
      type: k,
      last4: '8109',
    });
  }, []);

  const value = {
    balance,
    balanceFormatted: formatAmount(balance),
    transactions: decoratedTransactions,
    simoCard,
    applySimoCard,
    topUp,
    sendMoney,
    payQR,
    receiveMoney,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

export const useWallet = () => {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within a WalletProvider');
  return ctx;
};

/* ──────────────────────────────────────────────────────────────────
 * Helpers used by screens to render a transaction in the active language.
 * Pass the active `t` (from useLanguage) and one tx → get strings back.
 * ────────────────────────────────────────────────────────────────── */

export const txDisplayName = (tx, t) => {
  if (!tx) return '';
  // Top-up shape: prefix + translated method label
  if (tx.nameKey === 'txTopUpPrefix' && tx.methodKey) {
    return (t?.txTopUpPrefix || 'Top Up — ') + (t?.[tx.methodKey] || tx.methodKey);
  }
  if (tx.nameKey) return t?.[tx.nameKey] || tx.nameKey;
  return tx.name || '';
};

export const txCategoryLabel = (tx, t) => {
  if (!tx?.categoryKey) return '';
  return t?.[tx.categoryKey] || tx.categoryKey;
};

export const txTimeLabel = (tx, t) => {
  if (!tx?.timestamp) return '';
  const diffMs = Date.now() - tx.timestamp;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 30) return t?.justNow || 'Just now';
  if (sec < 60) return `${sec}${t?.secondsAgo || 's ago'}`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}${t?.minutesAgo || 'm ago'}`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}${t?.hoursAgo || 'h ago'}`;
  const day = Math.floor(hr / 24);
  if (day === 1) return t?.yesterday || 'Yesterday';
  if (day < 7) return `${day}${t?.daysAgo || 'd ago'}`;
  return new Date(tx.timestamp).toLocaleDateString();
};
